import { Alert, ChannelIcon } from "@audacia-hq/shared/components";
import { generateFileName, roundToNDigits } from "@audacia-hq/shared/utils";
import { faCircleCheck, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormikProvider, useFormik } from "formik";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { useAppContext } from "../../../context/AppContext";
import { useLocale } from "../../../context/LocaleContext";
import { useMember } from "../../../context/MemberContext";
import { GET_EXPERT_CONTENT } from "../../../graphql/query";
import { useFetcher } from "../../../hooks/useClientFetcher";
import { Expert, ExpertContent } from "../../../models/expert";
import { createWritten } from "../../../services/api";
import { uploadFile } from "../../../services/apiClients/storageAPI";
import config from "../../../services/config";
import Button from "../../Button";
import TextEllipsis, { BtnProps } from "../../TextEllipsis";
import { InputFile } from "../../form/InputFile";
import useMissingMemberData from "../MissingMemberDataHook";

import CreditTopUpForm from "./CreditTopUpForm";

interface Props {
  expert: Expert;
  onClose: () => void;
  onError: (err: string) => void;
  writtenMinuteCost: number;
}
const WrittenForm: React.FC<Props> = ({
  expert, onClose, onError, writtenMinuteCost,
}) => {
  const { t } = useTranslation("consultations");
  const locale = useLocale();
  const [loading, setLoading] = useState<boolean>(false);
  const [disconnected, setDisconnected] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [writtenCost, setWrittenCost] = useState<{ boost: boolean, value: number }>({ boost: false, value: 15 });
  const { member } = useMember();
  const { boosts } = useAppContext();

  const [_getExpertContent, { data, loading: contentLoading }] = useFetcher<{
    expert: { content: ExpertContent }
  }>(GET_EXPERT_CONTENT, {
    variables: {
      expertUid: expert.expertUid,
      profileLanguage: locale.toUpperCase(),
    },
    authenticated: true,
  });

  useEffect(() => {
    setDisconnected(!expert?.presenceStatus.writtenStatus);
  }, [expert]);

  useEffect(() => {
    if (!boosts) return;
    const writtenBoost = boosts.find((b) => b.channels.includes("WRITTEN"));
    if (!writtenBoost) return;
    setWrittenCost({ boost: true, value: roundToNDigits(0.15 * (100 - writtenBoost.value), 2) });
  }, [boosts]);

  const memberDataForm = useMissingMemberData({ pseudo: true, birthday: true });

  const maxFiles = 2;
  const maxFileSize = 4 * 1024 ** 2; // 4MB
  const mimeTypes = {
    "image/jpeg": [".jpeg", ".jpg"],
    "image/png": [".png"],
    "image/bmp": [".bmp"],
  };

  const formik = useFormik({
    initialValues: {
      subject: "", message: "", attachments: [], ...memberDataForm.initialValues,
    },
    validationSchema: Yup.object().shape({
      subject: Yup.string().required("required"),
      message: Yup.string().required("required"),
      ...memberDataForm.validationRules,
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (form, helpers) => {
      if (!expert) return;

      try {
        setLoading(true);

        await memberDataForm.onSubmit(form, helpers);

        const urls = await Promise.all(
          form.attachments.map((file) => uploadFile(file, generateFileName(file), "consultations/written", [expert.expertUid])),
        );
        const { error } = await createWritten({
          variables: {
            input: {
              subject: form.subject,
              message: form.message,
              attachments: urls,
              language: locale.toUpperCase(),
              expertUid: expert.expertUid,
            },
          },
        });
        if (error) {
          if (error.messages.find((m: string) => m === "expert.notAvailableForChannel")) {
            setDisconnected(true);
          } else {
            throw new Error("generalError");
          }
        } else {
          setConfirmed(true);
          localStorage.removeItem("consultationModal");
        }
      } catch (err) {
        toast.error(t("common:errors.generalError"));
      } finally {
        setLoading(false);
      }
    },
  });

  const renderConfirmed = () => (
    <>
      <div className="col-span-6 md:col-span-4 md:col-start-2">
        <div className="p-6">
          <div className="w-full text-center">
            <div className="text-2xl">{t("written.confirmationTitle")}</div>
            <FontAwesomeIcon
              className="text-6xl z-10 text-lime-500 my-6"
              icon={faCircleCheck}
            />
            <div className="text-lg">
              {t("written.confirmationDelay", {
                pseudo: expert?.pseudo,
                timeoutHours: config.consultations.written.timeoutHours,
              })}
            </div>
          </div>
          <div className="mt-9 text-center">
            <Button
              size="large"
              render="primary"
              onClick={() => onClose()}
              data-pw="writtenClose"
            >
              <span>{t("common:close")}</span>
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  const renderNoCredits = () => (
    <CreditTopUpForm
      channel="WRITTEN"
      remainingTime={member?.profile?.remainingTime}
      writtenMinuteCost={writtenMinuteCost}
    />
  );

  const ellipsisBtnFactory = (label: string, className?: string) => {
    const fc: React.FC<BtnProps> = ({ toggle }) => (
      <div className="text-center">
        <button type="button" onClick={toggle} className={className || ""}>
          {label}
        </button>
      </div>

    );
    return fc;
  };

  return (
    <>
      {confirmed && renderConfirmed()}
      {!confirmed && member.profile.remainingTime < writtenMinuteCost * 60 && renderNoCredits()}
      {!confirmed && member.profile.remainingTime >= writtenMinuteCost * 60 && (
        <>
          {disconnected && (
            <div className="mt-5">
              <Alert type="warning"><p>{t("written.expertDisconnected")}</p></Alert>
            </div>
          )}
          {!data && contentLoading ? (
            <div className="text-sm text-center p-6 bg-slate-100 rounded-sm mb-8">
              <FontAwesomeIcon className="mr-3" icon={faCircleNotch} spin />
            </div>
          ) : (
            <>
              {data?.expert.content.writtenMessage && (
                <div className="text-sm text-center p-6 bg-slate-100 rounded-sm mb-8">
                  <TextEllipsis
                    text={data.expert.content.writtenMessage}
                    length={130}
                    className="text-base"
                    showMore={ellipsisBtnFactory(
                      t("common:showMore"),
                      "text-sm font-bold underline underline-offset-4 mt-6",
                    )}
                    showLess={ellipsisBtnFactory(
                      t("common:showLess"),
                      "text-sm font-bold underline underline-offset-4 mt-6",
                    )}
                  />
                </div>
              )}
            </>
          )}

          <div className="border-l-2 border-slate-800 pl-3">
            <div>{t("written.cost", { minutes: writtenCost.value })}</div>
          </div>

          <FormikProvider value={formik}>
            {(() => {
              const inputFields = memberDataForm.inputs(formik);

              return (
                <div className="flex flex-col items-center gap-4 my-3 w-full">
                  {inputFields.pseudo && (
                    <div className="w-full flex flex-col gap-1 justify-stretch">
                      {inputFields.pseudo()}
                    </div>
                  )}
                  {inputFields.birthday && (
                    <div className="w-full flex flex-col gap-1 ">
                      {inputFields.birthday()}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="flex flex-col gap-1 my-3">
              <label htmlFor="subject" className="text-md">
                {t("written.subject")}
              </label>
              <input
                maxLength={500}
                className={`input ${formik.errors.subject ? "border-red-500 border" : ""}`}
                id="subject"
                value={formik.values.subject}
                onChange={formik.handleChange}
              />
              {formik.errors.subject && (
                <span className="text-red-500">
                  {t(`common:errors.${formik.errors.subject}`)}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-1 my-3">
              <label htmlFor="message" className="text-md">
                {t("written.message")}
              </label>
              <textarea
                maxLength={1400}
                className={`input ${formik.errors.message ? "border-red-500 border" : ""}`}
                id="message"
                rows={5}
                value={formik.values.message}
                onChange={formik.handleChange}
              />
              <div className="flex justify-between items-center w-full">
                <div className="text-red-500">
                  {formik.errors.message
                    && t(`common:errors.${formik.errors.message}`)}
                </div>
                <div className="text-right text-sm font-semibold">
                  {t("common:textLimit", {
                    limit: 1400 - formik.values.message.length,
                  })}
                </div>
              </div>
            </div>
            <div>
              <InputFile
                key="fileInput"
                className="w-full p-2 border rounded-lg my-3"
                name="attachments"
                value={formik.values.attachments}
                setFieldValue={formik.setFieldValue}
                setFieldTouched={formik.setFieldTouched}
                maxFiles={maxFiles}
                maxFileSize={maxFileSize}
                mimeTypes={mimeTypes}
              />
            </div>

            <div className="pt-6 text-center">
              <Button
                size="large"
                render="primary"
                onClick={() => formik.submitForm()}
                loading={loading}
                data-pw="consultationSubmit"
              >
                <span className="mr-3"><ChannelIcon channel="WRITTEN" className="text-white" /></span>
                <span>{t("common:send")}</span>
              </Button>
            </div>

          </FormikProvider>
        </>
      )}

    </>
  );
};

export default WrittenForm;
